<section class="stores-page">
  <div class="page-heading">
    <div>
      <p>{{ isPlatformPortal ? 'Platform operations' : 'Business setup' }}</p>
      <h2>{{ isPlatformPortal ? 'Platform Business Locations' : 'Business Locations' }}</h2>
    </div>
    @if (subscription) {
      <div class="plan-usage">
        <span>{{ subscription.plan }}</span>
        <strong>{{ subscription.activeStores }} / {{ subscription.maxStores }} locations</strong>
      </div>
    }
  </div>

  @if (successMessage) {
    <p class="state success">{{ successMessage }}</p>
  }
  @if (errorMessage) {
    <p class="state error">{{ errorMessage }}</p>
  }

  @if (canCreateLocation) { <section class="store-creator">
    <div class="form-side">
      <div class="section-title">
        <div>
          <h3>Create {{ isPlatformPortal ? 'platform business location' : 'business location' }}</h3>
          <p>Search and select the exact address. Location details are filled automatically.</p>
        </div>
      </div>

      <form (ngSubmit)="createStore()">
        <div class="form-grid">
          <label>
            <span>Location name</span>
            <input name="name" [(ngModel)]="form.name" placeholder="Mumbai showroom" required />
          </label>
          <label>
            <span>Location code</span>
            <input name="code" [(ngModel)]="form.code" placeholder="MUM-01" />
          </label>
          <label>
            <span>Phone</span>
            <input name="phone" type="tel" inputmode="numeric" autocomplete="tel" maxlength="10" pattern="[0-9]{10}" [ngModel]="form.phone" (ngModelChange)="phoneInput($event)" placeholder="10-digit phone number" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" name="email" [(ngModel)]="form.email" placeholder="store@example.in" />
          </label>
        </div>

        <div class="location-search">
          <label>
            <span>Find address on map</span>
            <div class="search-control">
              <input
                name="addressSearch"
                [(ngModel)]="addressSearch"
                (ngModelChange)="onAddressSearchChange($event)"
                placeholder="Search road, building, area or city"
                autocomplete="off"
              />
              <button type="button" (click)="searchAddress()" [disabled]="isSearching || isResolvingLocation">
                <span class="material-symbols-outlined" aria-hidden="true">search</span>
                {{ isSearching ? 'Searching...' : 'Search' }}
              </button>
              <button type="button" class="secondary icon-action" title="Use current location" (click)="useCurrentLocation()" [disabled]="isLocating">
                <span class="material-symbols-outlined" aria-hidden="true">my_location</span>
              </button>
            </div>
          </label>
          @if (suggestions.length) {
            <div class="suggestions">
              @for (suggestion of suggestions; track suggestion.placeId) {
                <button type="button" (click)="selectSuggestion(suggestion)">
                  <span class="material-symbols-outlined" aria-hidden="true">location_on</span>
                  {{ suggestion.description }}
                </button>
              }
            </div>
          }
          @if (isResolvingLocation) {
            <p class="resolving"><span class="material-symbols-outlined" aria-hidden="true">progress_activity</span> Loading complete address...</p>
          }
        </div>

        @if (selectedLocation) {
          <input type="hidden" name="addressLine1" [(ngModel)]="form.addressLine1" />
          <input type="hidden" name="latitude" [(ngModel)]="form.latitude" />
          <input type="hidden" name="longitude" [(ngModel)]="form.longitude" />

          <div class="resolved-location">
            <span class="material-symbols-outlined" aria-hidden="true">where_to_vote</span>
            <div>
              <strong>{{ selectedLocation.formattedAddress }}</strong>
              <p>{{ form.area || 'Area unavailable' }} · {{ form.city }} · {{ form.state }} · {{ form.country }} {{ form.postalCode }}</p>
            </div>
          </div>

          <div class="address-fields">
            <label>
              <span>Building / unit / floor (optional)</span>
              <input name="addressLine2" [(ngModel)]="form.addressLine2" placeholder="Building name, unit or floor" />
            </label>
            <label>
              <span>Area</span>
              <input name="area" [(ngModel)]="form.area" [readonly]="!isLocationFieldEditable('area')" placeholder="Enter area" />
            </label>
            <label>
              <span>City</span>
              <input name="city" [(ngModel)]="form.city" [readonly]="!isLocationFieldEditable('city')" placeholder="Enter city" />
            </label>
            <label>
              <span>State</span>
              <input name="state" [(ngModel)]="form.state" [readonly]="!isLocationFieldEditable('state')" placeholder="Enter state" />
            </label>
            <label>
              <span>Country</span>
              <input name="country" [(ngModel)]="form.country" [readonly]="!isLocationFieldEditable('country')" placeholder="Enter country" />
            </label>
            <label>
              <span>PIN code</span>
              <input name="postalCode" [(ngModel)]="form.postalCode" [readonly]="!isLocationFieldEditable('postalCode')" placeholder="Enter 6-digit PIN code" inputmode="numeric" />
            </label>
          </div>
        }

        <label class="primary-check">
          <input type="checkbox" name="isPrimary" [(ngModel)]="form.isPrimary" />
          <span>Make this the primary business location</span>
        </label>

        <button type="submit" class="submit" [disabled]="isSubmitting">
          <span class="material-symbols-outlined" aria-hidden="true">add_business</span>
          {{ isSubmitting ? 'Creating...' : 'Create location' }}
        </button>
        @if (submitErrorMessage) {
          <p class="state error submit-feedback" role="alert" aria-live="assertive">{{ submitErrorMessage }}</p>
        }
      </form>
    </div>

    <div class="map-side">
      @if (mapPreviewUrl) {
        <iframe title="Selected business location" [src]="mapPreviewUrl" loading="lazy"></iframe>
      } @else {
        <div class="map-empty">
          <span class="material-symbols-outlined" aria-hidden="true">map</span>
          <strong>No location selected</strong>
          <p>Search for an address or use your current location.</p>
        </div>
      }
    </div>
  </section> }

  <section class="saved-section">
    <div class="section-title">
      <div>
        <h3>Saved business locations</h3>
        <p>These locations are available in product creation.</p>
      </div>
      <button type="button" class="secondary" (click)="loadStores(true)">
        <span class="material-symbols-outlined" aria-hidden="true">refresh</span>
        Refresh
      </button>
    </div>

    @if (storeListErrorMessage) {
      <p class="state error">{{ storeListErrorMessage }}</p>
    }

    @if (isLoading) {
      <p class="loading">Loading business locations...</p>
    } @else {
      <div class="store-list">
        @for (store of stores; track store.id) {
          <article class="store-item">
            <div class="store-icon"><span class="material-symbols-outlined" aria-hidden="true">storefront</span></div>
            <div class="store-copy">
              <div class="store-name">
                <strong>{{ store.name }}</strong>
                @if (store.isPrimary) { <span>Primary</span> }
              </div>
              <p>{{ storeAddress(store) }}</p>
            </div>
            <div class="store-actions">
              @if (!store.isPrimary && canSetPrimaryLocation) {
                <button type="button" class="secondary" (click)="setPrimary(store)" [disabled]="busyStoreId === store.id">Set primary</button>
              }
              @if (canDeactivateLocation) { <button type="button" class="danger" title="Deactivate business location" (click)="deactivate(store)" [disabled]="busyStoreId === store.id">
                @if (busyStoreId === store.id) {
                  <span class="material-symbols-outlined action-spinner" aria-hidden="true">progress_activity</span>
                  Deactivating...
                } @else {
                  <span class="material-symbols-outlined" aria-hidden="true">block</span>
                  Deactivate
                }
              </button> }
            </div>
          </article>
        } @empty {
          <p class="loading">No business locations yet. Create your first location above.</p>
        }
      </div>
    }
  </section>
</section>
